import {
  selectAircraftScheduleTimelineEntriesSaga,
  selectContextMenuClickPosition,
  selectCopyItemSaga,
  selectCrewScheduleTimelineEntriesSaga,
  selectCutItemSaga,
  selectDutyScheduleTimelineEntriesSaga,
  selectMaintenanceScheduleTimelineEntriesSaga,
  selectMXAircraftScheduleTLEntriesSaga,
} from '../../selectors-saga';
import { CrewTimelineEntryType } from '../../../../../../../common/types/timeline/CrewTimelineEntryType';
import { AircraftTimelineEntryType } from '../../../../../../../common/types/timeline/AircraftTimelineEntryType';
import { call, put } from 'redux-saga/effects';
import { scheduleActions } from '../../actions';
import { CrewScheduleTimelineEntry } from '../../../../../../../common/types/timeline/CrewScheduleTimelineEntry';
import api from '../../../../../../../services/api';
import { fetchAircraftScheduleEntry, fetchEntry } from '../timelineEntries/fetchEntry';
import { CrewScheduleEntry } from 'redux/pages/crew-scheduling-v3/scheduleEntries/types/CrewScheduleEntry';
import { selectCompanyIDSaga } from '../../../../../../../common/saga/selectors';
import { showErrorModal } from '../../../legacy/utils/errHandling';
import { DutyTimelineEntry } from '../../../../../../../common/types/timeline/DutyTimelineEntry';
import { DutyScheduleEntry } from 'redux/pages/crew-scheduling-v3/scheduleEntries/types/DutyScheduleEntry';
import { selectFeatureFlagsSaga } from '../../../../../../../redux/common/selectors-saga';
import { CrewTimelineItem } from '../../../components/Timeline/CrewTimeline/generator';
import { AircraftTimelineItem } from '../../../components/Timeline/AircraftTimeline/generator';
import { ContextMenuClickPosition } from '../../../types/timeline';
import {
  AircraftSchedule,
  MaintenanceAircraftSchedule,
} from '../../../../../../../common/types/aircraftSchedule';
import { AircraftTimelineEntry } from '../../../../../../../common/types/timeline/AircraftTimelineEntry';
import { adjustEntryDetailedFlightTimes } from '../../../legacy/utils/utils';
import { NEW_ITEM_ID } from '../../../components/Timeline/constants';
import { adjustDutyTimes } from '../../../helpers';
import moment from 'moment-timezone';
import { parseGroupID } from '../../../components/Timeline/helper';
import { ContractTimelineItem } from '../../../components/Timeline/ContractTimeline/generator';
import { MaintenanceTimelineItem } from '../../../components/Timeline/MaintenanceTimeline/generator';
import { MaintenanceTimelineEntryType } from '../../../../../../../common/types/timeline/MaintenanceTimelineEntryType';
import { MaintenanceTimelineEntry } from '../../../../../../../common/types/timeline/MaintenanceEntryType';
import { MaintenanceScheduleEntry } from '../../../../../../../redux/pages/crew-scheduling-v3/scheduleEntries/types/MaintenanceScheduleEntry';
import { DetailedFlightTime } from '../../../../../../../common/types/CombinedEntry';
import { waitToFinishLoading } from '../timeline/waitToFinishLoading';
import { ContractTimelineEntryType } from 'common/types/timeline/ContractTimelineEntryType';

export function* handlePasteTimelineItem() {
  yield put(scheduleActions.setState({ operationsOngoing: true }));

  yield call(waitToFinishLoading);

  const cutItem = yield* selectCutItemSaga();
  const copyItem = yield* selectCopyItemSaga();
  const position = yield* selectContextMenuClickPosition();

  if (copyItem) {
    yield call(handleCopyAndPaste, copyItem, position);
  } else if (cutItem) {
    yield call(handleCutAndPaste, cutItem, position);
  }

  yield put(scheduleActions.setState({ operationsOngoing: false }));
}

function* handleCutAndPaste(
  cutItem: CrewTimelineItem | AircraftTimelineItem | MaintenanceTimelineItem,
  position: ContextMenuClickPosition,
) {
  const companyID = yield* selectCompanyIDSaga();

  const currentItemStart = cutItem.start_time;
  const currentItemEnd = cutItem.end_time;
  let [newItemStart, newItemEnd] = calculateNewItemTime(
    currentItemStart,
    currentItemEnd,
    position.time,
  );
  const selectedGroupEntryID = parseGroupID(position.groupID).entryID;

  switch (cutItem.entryType) {
    case CrewTimelineEntryType.CrewSchedule:
      const existedEntries = yield* selectCrewScheduleTimelineEntriesSaga();
      const updatedEntries = existedEntries.map(entry => {
        if (entry.ID === cutItem.id) {
          const updatedEntry: CrewScheduleTimelineEntry = {
            ...entry,
            StartTime: moment(newItemStart).format(),
            EndTime: moment(newItemEnd).format(),
            UserID: selectedGroupEntryID,
          };
          return updatedEntry;
        }
        return entry;
      });

      // here we should patch the entry
      try {
        const fullEntry = (yield fetchEntry(
          CrewTimelineEntryType.CrewSchedule,
          cutItem.id,
          false,
        )) as CrewScheduleEntry;
        fullEntry.StartTime = moment(newItemStart).format();
        fullEntry.EndTime = moment(newItemEnd).format();
        fullEntry.UserID = selectedGroupEntryID;
        yield call(
          api.patch,
          `/v1/companies/${companyID}/crewschedule/update/${fullEntry.ID}`,
          fullEntry,
        );
      } catch (error) {
        showErrorModal('Failed to update the entry', error);
      }

      yield put(scheduleActions.setState({ crewScheduleTimelineEntries: updatedEntries }));

      break;
    case MaintenanceTimelineEntryType.MaintenanceCrew:
      const existedMaintenanceEntries = yield* selectMaintenanceScheduleTimelineEntriesSaga();
      const updatedMaintenanceEntries = existedMaintenanceEntries.map(entry => {
        if (entry.ID === cutItem.id) {
          const updatedEntry: MaintenanceTimelineEntry = {
            ...entry,
            StartTime: moment(newItemStart).format(),
            EndTime: moment(newItemEnd).format(),
            UserID: selectedGroupEntryID,
          };
          return updatedEntry;
        }
        return entry;
      });

      try {
        const fullEntry = (yield fetchEntry(
          MaintenanceTimelineEntryType.MaintenanceCrew,
          cutItem.id,
          false,
        )) as MaintenanceTimelineEntry;
        fullEntry.StartTime = moment(newItemStart).format();
        fullEntry.EndTime = moment(newItemEnd).format();
        fullEntry.UserID = selectedGroupEntryID;
        yield api.patch(
          `/v1/companies/${companyID}/maintenance-crew-schedule/update/${fullEntry.ID}`,
          fullEntry,
        );
      } catch (error) {
        showErrorModal('Failed to update the entry', error);
      }

      yield put(
        scheduleActions.setState({ maintenanceTimelineEntries: updatedMaintenanceEntries }),
      );

      break;
    case CrewTimelineEntryType.DutySchedule:
      const featureFlags = yield* selectFeatureFlagsSaga();

      const existedDutyScheduleEntries = yield* selectDutyScheduleTimelineEntriesSaga();

      const updatedDutyScheduleEntries = existedDutyScheduleEntries.map(entry => {
        if (entry.ID === cutItem.id) {
          return adjustDutyTimes(
            { ...entry, IsAwaitingForUpdate: true, UserID: selectedGroupEntryID },
            cutItem.start_time,
            cutItem.end_time,
            newItemStart,
            newItemEnd,
            featureFlags,
          ) as DutyTimelineEntry;
        }
        return entry;
      });

      yield put(
        scheduleActions.setState({ dutyScheduleTimelineEntries: updatedDutyScheduleEntries }),
      );

      // here we should patch the entry
      try {
        let fullEntry = (yield fetchEntry(
          CrewTimelineEntryType.DutySchedule,
          cutItem.id,
          false,
        )) as DutyScheduleEntry;

        fullEntry = adjustDutyTimes(
          fullEntry,
          cutItem.start_time,
          cutItem.end_time,
          newItemStart,
          newItemEnd,
          featureFlags,
        ) as DutyScheduleEntry;
        fullEntry.UserID = selectedGroupEntryID;

        const withUpdatedDetailedFlightTimes = adjustEntryDetailedFlightTimes(fullEntry);

        yield call(
          api.patch,
          `/v1/users/${fullEntry.UserID}/duty-schedule/${fullEntry.ID}/v2`,
          withUpdatedDetailedFlightTimes,
        );
      } catch (error) {
        showErrorModal('Failed to update the entry', error);
      }

      break;
    case AircraftTimelineEntryType.AircraftSchedule:
      const existedAircraftScheduleEntries = yield* selectAircraftScheduleTimelineEntriesSaga();
      const updatedASEntries = existedAircraftScheduleEntries.map(entry => {
        if (entry.VirtualID == cutItem.aircraftEntry.VirtualID) {
          const updatedEntry: AircraftTimelineEntry = {
            ...entry,
            StartTime: new Date(newItemStart),
            EndTime: new Date(newItemEnd),
            AssignedAircraftIDs: [selectedGroupEntryID],
          };
          return updatedEntry;
        }
        return entry;
      });
      yield put(scheduleActions.setState({ aircraftScheduleTimelineEntries: updatedASEntries }));

      try {
        let fullASEntry = (yield fetchAircraftScheduleEntry(
          cutItem.entryType,
          cutItem,
          false,
        )) as AircraftSchedule;

        const crewScheduleEntries = fullASEntry.RelatedCrewScheduleEntries
          ? fullASEntry.RelatedCrewScheduleEntries
          : [];
        const crewScheduleEntryIDs = [];
        const crewScheduleTypes = [];
        crewScheduleEntries.forEach(entry => {
          crewScheduleEntryIDs.push(entry.ID);
          crewScheduleTypes.push({
            userID: entry.UserID,
            companyScheduleTypeID: entry.CompanyScheduleTypeID,
          });
          return entry;
        });
        const deletePayload = {
          CrewScheduleEntryIds: crewScheduleEntryIDs,
        };

        yield call(api.post, `/v1/companies/${companyID}/crewschedule/bulk-delete`, deletePayload);

        const bulkCreatePayload: Partial<
          AircraftSchedule | { UserIDs: string[]; AircraftID: string }
        > = {
          Notes: fullASEntry.Notes,
          UserIDs: fullASEntry.UserID,
          AircraftID: selectedGroupEntryID,
          StartAirportID: fullASEntry.StartAirportID,
          EndAirportID: fullASEntry.EndAirportID,
          CompanyScheduleTypeID: fullASEntry.CompanyScheduleTypeID,
          Subparts: fullASEntry.Subparts,
          StartTime: new Date(newItemStart),
          EndTime: new Date(newItemEnd),
          ScheduleRoleIDs: fullASEntry.ScheduleRoleIDs,
          UserScheduleTypes: crewScheduleTypes,
        };
        yield call(
          api.post,
          `/v1/companies/${companyID}/crewschedule/bulk-create`,
          bulkCreatePayload,
        );
      } catch (error) {
        showErrorModal('Failed to cut & paste the entry', error);
      }
      break;
    case AircraftTimelineEntryType.MaintenanceAircraftSchedule:
      const existedMXAircraftScheduleEntries = yield* selectMXAircraftScheduleTLEntriesSaga();
      const updatedMASEntries = existedMXAircraftScheduleEntries.map(entry => {
        if (entry.VirtualID == cutItem.maintenanceAircraftEntry.VirtualID) {
          const updatedEntry: AircraftTimelineEntry = {
            ...entry,
            StartTime: new Date(newItemStart),
            EndTime: new Date(newItemEnd),
            AssignedAircraftIDs: [selectedGroupEntryID],
          };
          return updatedEntry;
        }
        return entry;
      });
      yield put(scheduleActions.setState({ mxAircraftScheduleTimelineEntries: updatedMASEntries }));

      try {
        let fullMASEntry = (yield fetchAircraftScheduleEntry(
          cutItem.entryType,
          cutItem,
          false,
        )) as MaintenanceAircraftSchedule;

        const maintenanceScheduleEntries = fullMASEntry.RelatedMaintenanceScheduleEntries
          ? fullMASEntry.RelatedMaintenanceScheduleEntries
          : [];
        const maintenanceScheduleEntryIDs = [];
        maintenanceScheduleEntries.forEach(entry => {
          maintenanceScheduleEntryIDs.push(entry.ID);
          return entry;
        });
        const deletePayload = {
          MaintenanceScheduleEntryIds: maintenanceScheduleEntryIDs,
        };

        yield call(
          api.post,
          `/v1/companies/${companyID}/maintenance-crew-schedule/bulk-delete`,
          deletePayload,
        );

        const bulkCreatePayload: Partial<
          MaintenanceAircraftSchedule | { UserIDs: string[]; AircraftID: string }
        > = {
          Notes: fullMASEntry.Notes,
          UserIDs: fullMASEntry.UserID,
          AircraftID: selectedGroupEntryID,
          CompanyScheduleTypeID: fullMASEntry.CompanyScheduleTypeID,
          StartTime: new Date(newItemStart),
          EndTime: new Date(newItemEnd),
        };
        yield call(
          api.post,
          `/v1/companies/${companyID}/maintenance-crew-schedule/bulk-create`,
          bulkCreatePayload,
        );
      } catch (error) {
        showErrorModal('Failed to cut & paste the entry', error);
      }
      break;
    case CrewTimelineEntryType.DutyTimes:
      console.log('not implemented yet');
      break;
  }
}

function* handleCopyAndPaste(
  copyItem: CrewTimelineItem | AircraftTimelineItem | MaintenanceTimelineItem | any,
  position: ContextMenuClickPosition,
) {
  const companyID = yield* selectCompanyIDSaga();

  const currentItemStart = copyItem.start_time;
  const currentItemEnd = copyItem.end_time;
  let [newItemStart, newItemEnd] = calculateNewItemTime(
    currentItemStart,
    currentItemEnd,
    position.time,
  );

  let selectedGroupEntryID = position.groupID;
  if (copyItem.entryType !== ContractTimelineEntryType.Group) {
    selectedGroupEntryID = parseGroupID(position.groupID).entryID;
  }

  switch (copyItem.entryType) {
    case ContractTimelineEntryType.Group:
      try {
        const newEntry = {
          AircraftIDs: copyItem.aircraftIDs,
          BaseScheduleDetails: copyItem.baseScheduleDetails,
          Status: copyItem?.status || null,
          PrivateNotes: copyItem?.privateNotes || null,
          Notes: copyItem?.notes || null,
          DisplayedNote: copyItem?.displayedNote || null,
          InvoiceNumber: copyItem?.invoiceNumber || null,
          ClientContractID: copyItem.clientContractID,
          ClientID: copyItem.clientID,
          CompanyID: companyID,
          ContractDate: null,
          EndTime: moment(newItemEnd).format(),
          GroupID: selectedGroupEntryID,
          PilotIDs: copyItem.pilotIDs,
          SchedulePeriod: [moment(newItemStart), moment(newItemEnd)],
          StartTime: moment(newItemStart).format(),
          CopiedFromEntryID: copyItem.id,
        };
        yield call(api.post, `/v1/companies/${companyID}/aircraftcontract/create`, newEntry);
      } catch (err) {
        showErrorModal('Failed to copy the entry', err);
      }
      break;
    case CrewTimelineEntryType.CrewSchedule:
      const existedEntries = yield* selectCrewScheduleTimelineEntriesSaga();
      const existedCrewScheduleEntry = existedEntries.find(entry => entry.ID === copyItem.id);
      const newCrewScheduleEntry: CrewScheduleTimelineEntry = {
        ...existedCrewScheduleEntry,
        ID: NEW_ITEM_ID,
        StartTime: moment(newItemStart).format(),
        EndTime: moment(newItemEnd).format(),
        UserID: selectedGroupEntryID,
      };

      const updatedEntries = [...existedEntries, newCrewScheduleEntry];
      yield put(scheduleActions.setState({ crewScheduleTimelineEntries: updatedEntries }));

      try {
        const fullEntry = (yield fetchEntry(
          CrewTimelineEntryType.CrewSchedule,
          copyItem.id,
          false,
        )) as CrewScheduleEntry;

        fullEntry.StartTime = moment(newItemStart).format();
        fullEntry.EndTime = moment(newItemEnd).format();
        fullEntry.UserID = selectedGroupEntryID;
        yield call(api.post, `/v1/companies/${companyID}/crewschedule/create`, fullEntry);
      } catch (err) {
        showErrorModal('Failed to copy the entry', err);
      }
      break;
    case MaintenanceTimelineEntryType.MaintenanceCrew:
      const existedMaintenanceEntries = yield* selectMaintenanceScheduleTimelineEntriesSaga();
      const existedMaintenanceScheduleEntry = existedMaintenanceEntries.find(
        entry => entry.ID === copyItem.id,
      );
      const newMaintenanceScheduleEntry: MaintenanceTimelineEntry = {
        ...existedMaintenanceScheduleEntry,
        ID: NEW_ITEM_ID,
        StartTime: moment(newItemStart).format(),
        EndTime: moment(newItemEnd).format(),
        UserID: selectedGroupEntryID,
      };
      const updatedMaintenanceEntries = [...existedMaintenanceEntries, newMaintenanceScheduleEntry];
      yield put(
        scheduleActions.setState({ maintenanceTimelineEntries: updatedMaintenanceEntries }),
      );

      try {
        const fullEntry = (yield fetchEntry(
          MaintenanceTimelineEntryType.MaintenanceCrew,
          copyItem.id,
          false,
        )) as MaintenanceScheduleEntry;

        fullEntry.StartTime = moment(newItemStart).format();
        fullEntry.EndTime = moment(newItemEnd).format();
        fullEntry.UserID = selectedGroupEntryID;
        yield api.post(`/v1/companies/${companyID}/maintenance-crew-schedule/create`, fullEntry);
      } catch (err) {
        showErrorModal('Failed to copy the entry', err);
      }
      break;
    case CrewTimelineEntryType.DutySchedule:
      const existedDSEntries = yield* selectDutyScheduleTimelineEntriesSaga();
      const featureFlags = yield* selectFeatureFlagsSaga();

      const existedDSEntry = existedDSEntries.find(entry => entry.ID === copyItem.id);

      const updatedDSEntry = adjustDutyTimes(
        { ...existedDSEntry },
        copyItem.start_time,
        copyItem.end_time,
        newItemStart,
        newItemEnd,
        featureFlags,
      ) as DutyTimelineEntry;
      updatedDSEntry.ID = NEW_ITEM_ID;
      updatedDSEntry.IsAwaitingForUpdate = true;
      updatedDSEntry.UserID = selectedGroupEntryID;

      const updatedDutyScheduleEntries = [...existedDSEntries, updatedDSEntry];
      yield put(
        scheduleActions.setState({ dutyScheduleTimelineEntries: updatedDutyScheduleEntries }),
      );

      try {
        let fullEntry = (yield fetchEntry(
          CrewTimelineEntryType.DutySchedule,
          copyItem.id,
          false,
        )) as DutyScheduleEntry;

        let oldEntryCopy = { ...fullEntry };
        fullEntry = adjustDutyTimes(
          fullEntry,
          copyItem.start_time,
          copyItem.end_time,
          newItemStart,
          newItemEnd,
          featureFlags,
        ) as DutyScheduleEntry;
        fullEntry.ID = null;
        fullEntry.UserID = selectedGroupEntryID;

        // adjust detailed flight times if there are any
        if (Array.isArray(fullEntry.AircraftInformation.FlightInformation)) {
          for (const [
            i,
            aircraftInfo,
          ] of fullEntry.AircraftInformation.FlightInformation.entries()) {
            if (Array.isArray(aircraftInfo.DetailedFlightTimes)) {
              for (const [j, fltEntry] of aircraftInfo.DetailedFlightTimes.entries()) {
                let res = normalizeDetailedFltTimes(
                  moment.utc(oldEntryCopy.StartTime),
                  fltEntry,
                  moment.utc(fullEntry.StartTime),
                );
                fullEntry.AircraftInformation.FlightInformation[i].DetailedFlightTimes[j] = res;
              }
            }
          }
        }

        yield call(api.post, `/v1/users/${fullEntry.UserID}/duty-schedule/v2`, fullEntry);
      } catch (err) {
        showErrorModal('Failed to copy the entry', err);
      }

      break;
    case CrewTimelineEntryType.DutyTimes:
      console.log('It`s probably should not be implemented');
      break;
    case AircraftTimelineEntryType.AircraftSchedule:
      const existedAircraftScheduleEntries = yield* selectAircraftScheduleTimelineEntriesSaga();
      let existedTimelineEntry: AircraftTimelineEntry;
      existedTimelineEntry = existedAircraftScheduleEntries.find(
        entry => entry.VirtualID === copyItem.aircraftEntry.VirtualID,
      );
      if (!existedTimelineEntry) {
        yield put(scheduleActions.setState({ isShowTimelineLoaded: true }));
        try {
          existedTimelineEntry = yield call(
            fetchAircraftScheduleEntry,
            copyItem.entryType,
            copyItem,
            true,
          );
        } catch (err) {
          showErrorModal('Failed to copy the entry', err);
        } finally {
          yield put(scheduleActions.setState({ isShowTimelineLoaded: false }));
        }
      }
      const newASTimelineEntry = {
        ...existedTimelineEntry,
        StartTime: new Date(newItemStart),
        EndTime: new Date(newItemEnd),
        AssignedAircraftIDs: [selectedGroupEntryID],
      };
      newASTimelineEntry.VirtualID = GenerateVirtualID(newASTimelineEntry);

      const updatedASEntries = [...existedAircraftScheduleEntries, newASTimelineEntry];
      yield put(scheduleActions.setState({ aircraftScheduleTimelineEntries: updatedASEntries }));

      try {
        let fullASEntry = (yield fetchAircraftScheduleEntry(
          copyItem.entryType,
          copyItem,
          false,
        )) as AircraftSchedule;

        const crewScheduleEntries = fullASEntry.RelatedCrewScheduleEntries
          ? fullASEntry.RelatedCrewScheduleEntries
          : [];
        const crewScheduleEntryIDs = [];
        const crewScheduleTypes = [];
        crewScheduleEntries.forEach(entry => {
          crewScheduleEntryIDs.push(entry.ID);
          crewScheduleTypes.push({
            userID: entry.UserID,
            companyScheduleTypeID: entry.CompanyScheduleTypeID,
          });
          return entry;
        });

        const bulkCreatePayload: Partial<
          AircraftSchedule | { UserIDs: string[]; AircraftID: string }
        > = {
          Notes: fullASEntry.Notes,
          UserIDs: fullASEntry.UserID,
          AircraftID: selectedGroupEntryID,
          StartAirportID: fullASEntry.StartAirportID,
          EndAirportID: fullASEntry.EndAirportID,
          CompanyScheduleTypeID: fullASEntry.CompanyScheduleTypeID,
          Subparts: fullASEntry.Subparts,
          StartTime: new Date(newItemStart),
          EndTime: new Date(newItemEnd),
          ScheduleRoleIDs: fullASEntry.ScheduleRoleIDs,
          UserScheduleTypes: crewScheduleTypes,
        };
        yield call(
          api.post,
          `/v1/companies/${companyID}/crewschedule/bulk-create`,
          bulkCreatePayload,
        );
      } catch (error) {
        showErrorModal('Failed to copy the entry', error);
      }
      break;
    case AircraftTimelineEntryType.MaintenanceAircraftSchedule:
      const existedMXAircraftScheduleEntries = yield* selectMXAircraftScheduleTLEntriesSaga();
      const existedMXTimelineEntry = existedMXAircraftScheduleEntries.find(
        entry => entry.VirtualID === copyItem.maintenanceAircraftEntry.VirtualID,
      );
      const newMASTimelineEntry = {
        ...existedMXTimelineEntry,
        StartTime: new Date(newItemStart),
        EndTime: new Date(newItemEnd),
        AssignedAircraftIDs: [selectedGroupEntryID],
      };
      console.log(newMASTimelineEntry);
      newMASTimelineEntry.VirtualID = GenerateVirtualID(newMASTimelineEntry);

      const updatedMASEntries = [...existedMXAircraftScheduleEntries, newMASTimelineEntry];
      yield put(scheduleActions.setState({ mxAircraftScheduleTimelineEntries: updatedMASEntries }));

      try {
        let fullMASEntry = (yield fetchAircraftScheduleEntry(
          copyItem.entryType,
          copyItem,
          false,
        )) as MaintenanceAircraftSchedule;

        const bulkCreatePayload: Partial<
          MaintenanceAircraftSchedule | { UserIDs: string[]; AircraftID: string }
        > = {
          Notes: fullMASEntry.Notes,
          UserIDs: fullMASEntry.UserID,
          AircraftID: selectedGroupEntryID,
          CompanyScheduleTypeID: fullMASEntry.CompanyScheduleTypeID,
          StartTime: new Date(newItemStart),
          EndTime: new Date(newItemEnd),
        };
        yield call(
          api.post,
          `/v1/companies/${companyID}/maintenance-crew-schedule/bulk-create`,
          bulkCreatePayload,
        );
      } catch (error) {
        showErrorModal('Failed to copy the entry', error);
      }
      break;
  }
}

export const GenerateVirtualID = (entry: AircraftTimelineEntry): string => {
  // Convert time values to string
  const startTimeUnix = Math.floor(entry.StartTime.getTime() / 1000);
  const endTimeUnix = Math.floor(entry.EndTime.getTime() / 1000);

  // Join AssignedUserIDs and AssignedAircraftIDs into single strings
  entry.AssignedUserIDs.sort();
  entry.AssignedAircraftIDs.sort();

  const userIDsStr = entry.AssignedUserIDs.join(',');
  const aircraftIDsStr = entry.AssignedAircraftIDs.join(',');

  // Combine all fields into one string with delimiters
  return `${startTimeUnix}|${endTimeUnix}|${userIDsStr}|${aircraftIDsStr}`;
};

export const GenerateVirtualIDForPilotSchedule = (entry: ContractTimelineItem): string => {
  // Convert time values to string
  const startTimeUnix = Math.floor(entry.start_time / 1000);
  const endTimeUnix = Math.floor(entry.end_time / 1000);

  // Join AssignedUserIDs and AssignedAircraftIDs into single strings
  entry.userIDs?.sort();

  const userIDsStr = entry.userIDs?.length > 0 ? entry.userIDs.join(',') : entry.userID;
  const groupedByID = entry.clientContractID;

  // Combine all fields into one string with delimiters
  return `${startTimeUnix}|${endTimeUnix}|${userIDsStr}|${groupedByID}`;
};

function calculateNewItemTime(
  currentItemStart: number,
  currentItemEnd: number,
  time: number,
): [number, number] {
  const startHours = moment(currentItemStart).hours();
  const startMinutes = moment(currentItemStart).minutes();

  const timeDifference = moment(currentItemEnd).diff(moment(currentItemStart), 's');

  const newStart = moment(time).set({ hour: startHours, minute: startMinutes });
  const newEnd = newStart.clone().add(timeDifference, 's');
  return [newStart.valueOf(), newEnd.valueOf()];
}

function normalizeDetailedFltTimes(
  oldTimestamp: moment.Moment,
  oldFltEntry: DetailedFlightTime,
  newTimestamp: moment.Moment,
): DetailedFlightTime {
  let diffDelta = Math.abs(oldTimestamp.diff(moment.utc(oldFltEntry.TimeOff), 'minute'));
  const timeOff = newTimestamp.clone().add(diffDelta, 'minute');

  diffDelta = Math.abs(oldTimestamp.diff(moment.utc(oldFltEntry.TimeOn), 'minute'));
  const timeOn = newTimestamp.clone().add(diffDelta, 'minute');

  return {
    ...oldFltEntry,
    TimeOff: timeOff.toDate(),
    TimeOn: timeOn.toDate(),
  };
}
